"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { ArticleItem } from "../BlogInit";

const TAG_COLORS: Record<string, string> = {
  "TikTok": "bg-pink-500/15 text-pink-300 border-pink-500/25",
  "YouTube": "bg-red-500/15 text-red-300 border-red-500/25",
  "Instagram": "bg-amber-500/15 text-amber-300 border-amber-500/25",
  "Tutorial": "bg-emerald-500/15 text-emerald-300 border-emerald-500/25",
  "Growth": "bg-sky-500/15 text-sky-300 border-sky-500/25",
  "Faceless": "bg-violet-500/15 text-violet-300 border-violet-500/25",
  "Monetization": "bg-yellow-500/15 text-yellow-300 border-yellow-500/25",
  "AI Tools": "bg-cyan-500/15 text-cyan-300 border-cyan-500/25",
  "Subtitles": "bg-teal-500/15 text-teal-300 border-teal-500/25",
  "Voiceover": "bg-indigo-500/15 text-indigo-300 border-indigo-500/25",
  "Horror": "bg-orange-500/15 text-orange-300 border-orange-500/25",
  "Music": "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/25",
  "Veo 3": "bg-blue-500/15 text-blue-300 border-blue-500/25",
  "Strategy": "bg-lime-500/15 text-lime-300 border-lime-500/25",
  "AI Video": "bg-purple-500/15 text-purple-300 border-purple-500/25",
};

const DEFAULT_TAG_COLOR = "bg-slate-500/15 text-slate-300 border-slate-500/25";

function TagPill({ tag }: { tag: string }) {
  const color = TAG_COLORS[tag] || DEFAULT_TAG_COLOR;
  return (
    <span className={`inline-block text-[10px] font-medium tracking-wide border rounded-full px-2 py-0.5 ${color}`}>
      {tag}
    </span>
  );
}

function formatDate(publishDate?: string): string {
  if (!publishDate) return "";
  const ts = Date.parse(publishDate);
  if (isNaN(ts)) return publishDate || "";
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ArticleListPaginated({
  articles,
  currentPage,
  totalPages,
  showFeatured,
}: {
  articles: ArticleItem[];
  currentPage: number;
  totalPages: number;
  showFeatured: boolean;
}) {
  const featured = showFeatured ? articles[0] : null;
  const grid = showFeatured ? articles.slice(1) : articles;

  const prevPage = currentPage > 1 ? (currentPage === 2 ? "/blog" : `/blog/page/${currentPage - 1}`) : null;
  const nextPage = currentPage < totalPages ? `/blog/page/${currentPage + 1}` : null;

  return (
    <div className="w-full flex flex-col items-center pb-20">
      <div className="w-full max-w-5xl px-5 sm:px-8">

        {/* Featured article (page 1 only) */}
        {featured && (
          <Link href={"/blog/" + featured.slug} className="group relative block w-full rounded-2xl overflow-hidden mb-10 md:mb-14">
            <div className="relative aspect-[2/1] md:aspect-[2.5/1]">
              <Image src={featured.coverURL!} alt={featured.title} fill sizes="(max-width: 1024px) 100vw, 1024px" className="object-cover transition-transform duration-500 group-hover:scale-[1.03]" priority />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-8 md:p-10">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.2em] text-violet-300 bg-violet-500/20 border border-violet-500/30 rounded-full px-3 py-1">Latest</span>
                  {featured.tags.map((tag) => <TagPill key={tag} tag={tag} />)}
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white leading-tight max-w-2xl">{featured.title}</h2>
                <p className="mt-2 text-sm text-slate-300">{formatDate(featured.publishDate)}</p>
              </div>
            </div>
          </Link>
        )}

        {/* Article grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {grid.map((a, i) => (
            <React.Fragment key={a.slug}>
              <Link href={"/blog/" + a.slug} className="group flex flex-col">
                <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-800">
                  <Image src={a.coverURL!} alt={a.title} fill sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw" className="object-cover transition-transform duration-500 group-hover:scale-[1.04]" loading="lazy" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
                </div>
                <div className="flex flex-col pt-3 gap-1.5">
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-slate-500 font-medium">{formatDate(a.publishDate)}</p>
                    {a.tags.length > 0 && <span className="text-slate-700 text-xs">|</span>}
                    <div className="flex flex-wrap gap-1.5">{a.tags.map((tag) => <TagPill key={tag} tag={tag} />)}</div>
                  </div>
                  <h3 className="font-semibold text-[15px] leading-snug text-slate-100 group-hover:text-violet-300 transition-colors duration-200 line-clamp-2">{a.title}</h3>
                  <span className="text-xs font-medium text-violet-400 group-hover:text-violet-300 transition-colors duration-200">Read article</span>
                </div>
              </Link>

              {/* Mid-page CTA (after 6th item, page 1 only) */}
              {showFeatured && i === 5 && (
                <div className="sm:col-span-2 lg:col-span-3 my-4 md:my-8">
                  <div className="relative rounded-2xl border border-white/[0.06] bg-gradient-to-br from-violet-500/[0.07] via-slate-900 to-slate-900 px-6 py-8 md:px-12 md:py-10 overflow-hidden">
                    <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[500px] h-[200px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" />
                    <div className="relative flex flex-col md:flex-row items-center justify-between gap-5">
                      <div className="text-center md:text-left">
                        <h3 className="text-lg md:text-xl font-bold text-white">Ready to create your first AI video?</h3>
                        <p className="mt-1 text-sm text-slate-400 max-w-md">Script, images, voice-over and subtitles — all automated. Start for free, no credit card needed.</p>
                      </div>
                      <Link href="/signup" className="shrink-0 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold px-6 py-2.5 rounded-xl transition-colors duration-200">
                        Try Vexub Free
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
                      </Link>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Pagination */}
        <nav className="mt-12 flex items-center justify-center gap-4" aria-label="Blog pagination">
          {prevPage ? (
            <Link href={prevPage} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-800/60 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-700/60 hover:border-white/20">
              <svg className="w-4 h-4 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
              Previous
            </Link>
          ) : <div />}

          <span className="text-sm text-slate-500">Page {currentPage} of {totalPages}</span>

          {nextPage ? (
            <Link href={nextPage} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-slate-800/60 px-5 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-700/60 hover:border-white/20">
              Next
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </Link>
          ) : <div />}
        </nav>

        {/* Bottom CTA */}
        <div className="mt-16 md:mt-20 text-center">
          <div className="inline-flex flex-col items-center gap-4">
            <p className="text-xs uppercase tracking-[0.25em] text-slate-500 font-medium">Start creating today</p>
            <h3 className="text-2xl md:text-3xl font-bold text-white max-w-lg leading-tight">Turn your ideas into scroll-stopping videos.</h3>
            <p className="text-sm text-slate-400 max-w-md">Join thousands of creators using Vexub to produce faceless TikTok, Reels and Shorts in minutes.</p>
            <Link href="/signup" className="mt-2 inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold px-7 py-3 rounded-xl transition-colors duration-200">
              Get Started Free
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
            </Link>
            <p className="text-xs text-slate-500">No credit card required</p>
          </div>
        </div>

      </div>
    </div>
  );
}
